import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export type FileReadMode = 'text' | 'base64' | 'dataUrl' | 'file';

export interface SelectedFileContent {
  file: File;
  name: string;
  extension: string;
  mimeType: string;
  size: number;
  content?: string;
}

@Component({
  selector: 'idba-file-selector',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './file-selector.component.html',
  styleUrls: ['./file-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileSelectorComponent {
  @Input() accept = '';
  @Input() extensions: string[] = [];
  @Input() mimeTypes: string[] = [];
  @Input() maxSize?: number;
  @Input() readMode: FileReadMode = 'text';
  @Input() disabled = false;
  @Input() buttonLabel = 'Selecionar arquivo';

  @Output() fileSelected = new EventEmitter<SelectedFileContent>();
  @Output() fileError = new EventEmitter<string>();

  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const extension = this.getExtension(file.name);
    const extensions = this.extensions.map((item) => item.replace(/^\./, '').toLowerCase());
    if (extensions.length && !extensions.includes(extension)) {
      this.fileError.emit(`Extensão inválida. Utilize: ${this.extensions.join(', ')}.`);
      return;
    }
    if (this.mimeTypes.length && file.type && !this.mimeTypes.includes(file.type)) {
      this.fileError.emit('Tipo de arquivo inválido.');
      return;
    }
    if (this.maxSize && file.size > this.maxSize) {
      this.fileError.emit(`O arquivo excede o tamanho máximo de ${this.formatSize(this.maxSize)}.`);
      return;
    }

    if (this.readMode === 'file') {
      this.emitFile(file, extension);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      let content = String(reader.result ?? '');
      if (this.readMode === 'base64') content = content.split(',')[1] ?? '';
      this.emitFile(file, extension, content);
    };
    reader.onerror = () => this.fileError.emit('Não foi possível ler o arquivo selecionado.');

    if (this.readMode === 'text') reader.readAsText(file, 'UTF-8');
    else reader.readAsDataURL(file);
  }

  private emitFile(file: File, extension: string, content?: string): void {
    this.fileError.emit('');
    this.fileSelected.emit({
      file,
      name: file.name,
      extension,
      mimeType: file.type,
      size: file.size,
      content,
    });
  }

  private getExtension(name: string): string {
    const index = name.lastIndexOf('.');
    return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
}
